import {
  DefaultChatTransport,
  isTextUIPart,
  readUIMessageStream,
  type UIMessage,
} from "ai";
import { useCallback, useRef } from "react";

import { authFetch } from "@phoenix/authFetch";
import { useAgentStore } from "@phoenix/contexts/AgentContext";

import {
  SUMMARY_OUTPUT_TOOL,
  SUMMARY_SYSTEM_PROMPT,
} from "./sessionSummaryPrompt";
import {
  getFirstAssistantMessageText,
  getFirstUserMessageText,
} from "./sessionSummaryUtils";

/**
 * Sends a lightweight summarization request through a {@link DefaultChatTransport}
 * and extracts the result from the forced tool call.
 *
 * The summary tool is sent as an `output_tool` (not a regular `tool`), which
 * tells the server to set `tool_choice: required` on the LLM request. This
 * guarantees the model produces structured output via the tool schema rather
 * than free-form text — critical for lesser models that tend to ignore prompt
 * instructions.
 *
 * Stream parsing is delegated to the AI SDK's {@link readUIMessageStream},
 * the same pipeline that powers `useChat`.
 */
async function fetchSummary({
  chatApiUrl,
  userMessageText,
  assistantMessageText,
}: {
  chatApiUrl: string;
  userMessageText: string;
  assistantMessageText: string;
}): Promise<string> {
  const transport = new DefaultChatTransport({
    api: chatApiUrl,
    fetch: authFetch,
    prepareSendMessagesRequest: ({
      body,
      id,
      messages,
      trigger,
      messageId,
    }) => ({
      body: {
        ...body,
        id,
        messages,
        trigger,
        messageId,
        traceNameSuffix: "Summary",
      },
    }),
  });

  const summarizePrompt = [
    "Summarize this conversation in 5-10 words:",
    `User: ${userMessageText}`,
    `Assistant: ${assistantMessageText}`,
  ].join("\n");

  const chunkStream = await transport.sendMessages({
    trigger: "submit-message",
    chatId: crypto.randomUUID(),
    messageId: undefined,
    body: {
      output_tools: [SUMMARY_OUTPUT_TOOL],
    },
    messages: [
      {
        id: crypto.randomUUID(),
        role: "system" as const,
        parts: [{ type: "text" as const, text: SUMMARY_SYSTEM_PROMPT }],
      },
      {
        id: crypto.randomUUID(),
        role: "user" as const,
        parts: [{ type: "text" as const, text: summarizePrompt }],
      },
    ],
    abortSignal: undefined,
  });

  let finalMessage: UIMessage | undefined;
  for await (const message of readUIMessageStream({ stream: chunkStream })) {
    finalMessage = message;
  }

  if (!finalMessage) return "";

  // The model is forced to call the summary output tool via tool_choice.
  // Extract the summary from the tool invocation's input.
  for (const part of finalMessage.parts) {
    if (part.type === "dynamic-tool" && part.toolName === "summary") {
      const input = part.input as { summary?: string } | undefined;
      if (input?.summary) return input.summary.trim();
    }
  }

  // Fallback: extract from text if the model responded with text despite
  // the forced tool_choice (shouldn't happen, but defensive).
  return finalMessage.parts
    .filter(isTextUIPart)
    .map((p) => p.text)
    .join("")
    .trim();
}

/**
 * Hook that provides a function to asynchronously generate a session summary
 * after the first assistant response completes.
 *
 * The summary is generated by making a separate, lightweight call to the
 * same `/chat` endpoint using the currently selected model. Stream parsing
 * is delegated to the AI SDK's {@link DefaultChatTransport} and
 * {@link readUIMessageStream} — the same pipeline that powers `useChat` —
 * so we don't maintain any custom protocol parsing.
 *
 * @param params.chatApiUrl - the chat endpoint URL (includes model params)
 */
export function useGenerateSessionSummary({
  chatApiUrl,
}: {
  chatApiUrl: string;
}) {
  const store = useAgentStore();
  // Track which sessions have already had summaries requested to avoid
  // duplicate fire-and-forget calls.
  const requestedSessionsRef = useRef(new Set<string>());

  const generateSummary = useCallback(
    ({ sessionId }: { sessionId: string }) => {
      // Only generate once per session
      if (requestedSessionsRef.current.has(sessionId)) return;

      const state = store.getState();
      const session = state.sessionMap[sessionId];
      if (!session) return;

      // Only generate if there's no summary yet
      if (session.shortSummary) return;

      const userText = getFirstUserMessageText(session.messages);
      const assistantText = getFirstAssistantMessageText(session.messages);
      if (!userText || !assistantText) return;

      requestedSessionsRef.current.add(sessionId);

      // Fire-and-forget: don't block the UI
      void fetchSummary({
        chatApiUrl,
        userMessageText: userText,
        assistantMessageText: assistantText,
      })
        .then((summary) => {
          if (summary) {
            store.getState().updateSessionSummary(sessionId, summary);
          }
        })
        .catch(() => {
          // Summary generation is best-effort; silently swallow errors.
          // Remove from the set so a retry can happen if the user triggers
          // another exchange.
          requestedSessionsRef.current.delete(sessionId);
        });
    },
    [chatApiUrl, store]
  );

  return { generateSummary };
}
